import React, { useMemo, useState } from 'react';
import { useClassName, useWindowSize } from 'common/hooks';
import { ContentfulRichText, SVGIcon } from 'common/components';
import useCustomStyle from '../../hooks';

import './faqs-list.less';
import { getStylesByPrefix } from '../../utils';
import Link from '../Link';
import { LinkTabs } from 'public/components/Tabs';

function FaqItem({
  faq,
  index,
  isDesktop,
  selectedFaqIndex,
  handleShowAnswer,
  questionStyles,
  questionSelectedStyles,
  questionTextStyles,
  answerStyles,
  answerTextStyles,
  classNames,
}) {
  const [, ctaStyles] = useCustomStyle(faq?.fields?.cta?.fields?.settings);
  const linkStyles = getStylesByPrefix('--link-', ctaStyles);
  const mobileModifier =
    selectedFaqIndex === index ? 'rotate-up' : 'rotate-down';

  return (
    <div
      key={`faq-${index}`}
      className={classNames(
        'faqs__item',
        selectedFaqIndex === index && isDesktop ? 'selected' : ''
      )}
      onClick={() => handleShowAnswer(index)}
      style={{
        ...questionStyles,
        ...(selectedFaqIndex === index && isDesktop
          ? questionSelectedStyles
          : {}),
      }}>
      <div className={classNames('faqs__item__question')}>
        <p
          className={classNames('faqs__item__question__text')}
          style={questionTextStyles}>
          {faq.fields.question}
        </p>
        <SVGIcon
          className={classNames(
            'faqs__item__question__icon',
            !isDesktop ? mobileModifier : ''
          )}
          name="chevron-right-mid"
        />
      </div>
      {!isDesktop && selectedFaqIndex === index && faq?.fields?.answer && (
        <div className={classNames('answer')} style={answerStyles}>
          <div className={classNames('answer__text')} style={answerTextStyles}>
            <ContentfulRichText field={{ 'en-US': faq.fields.answer }} />
          </div>
          {faq.fields.cta && (
            <Link style={linkStyles} component={faq.fields.cta} />
          )}
        </div>
      )}
    </div>
  );
}

export default function FaqsList({ component }) {
  const [, style] = useCustomStyle(component.fields.settings);
  const [selectedFaqIndex, setSelectedFaqIndex] = useState(0);
  const [selectedLink, setSelectedLink] = useState(0);
  const classNames = useClassName('CLPFaqsList');
  const { isDesktop } = useWindowSize();

  const handleShowAnswer = (index) => {
    if (!isDesktop) {
      if (index === selectedFaqIndex) {
        setSelectedFaqIndex(null);
        return;
      }
    }
    setSelectedFaqIndex(index);
  };

  const questionStyles = getStylesByPrefix('--question-', style);
  const questionSelectedStyles = getStylesByPrefix(
    '--question-selected-',
    style
  );
  const answerStyles = getStylesByPrefix('--answer-', style);
  const answerTextStyles = getStylesByPrefix('--answer-text-', style);
  const containerStyles = getStylesByPrefix('--container-', style);
  const questionTextStyles = getStylesByPrefix('--question-text-', style);

  const types = component.fields.faqs.map((faq) => faq.fields.types).flat();

  const links = useMemo(
    () =>
      [...new Set(types)].map((name) => ({
        url: '#',
        title: name,
      })),
    [types]
  );

  const faqsToShow = useMemo(() => {
    return component.fields.faqs.filter((faq) =>
      faq.fields.types.includes(links[selectedLink].title)
    );
  }, [component.fields.faqs, selectedLink, links]);

  const [, selectedFaqCtaStyles] = useCustomStyle(
    faqsToShow[selectedFaqIndex]?.fields?.cta?.fields?.settings
  );
  const linkStylesDesktop = getStylesByPrefix('--link-', selectedFaqCtaStyles);

  return (
    <div className={classNames('container')} style={containerStyles}>
      {links && (
        <div className={classNames('links')}>
          {
            <LinkTabs
              styles={{
                container: classNames('links__container'),
                linkClassName: classNames('links__item'),
              }}
              links={links}
              isSelected={(index) => index === selectedLink}
              fixed={false}
              onSelect={(e, index) => {
                e.preventDefault();
                setSelectedLink(index);
                setSelectedFaqIndex(0);
              }}
            />
          }
        </div>
      )}
      <div className={classNames('content')}>
        <div className={classNames('faqs')}>
          {faqsToShow.map((faq, index) => {
            return (
              <FaqItem
                key={`faq-${index}`}
                faq={faq}
                index={index}
                isDesktop={isDesktop}
                selectedFaqIndex={selectedFaqIndex}
                handleShowAnswer={handleShowAnswer}
                questionStyles={questionStyles}
                questionSelectedStyles={questionSelectedStyles}
                questionTextStyles={questionTextStyles}
                answerStyles={answerStyles}
                answerTextStyles={answerTextStyles}
                classNames={classNames}
              />
            );
          })}
        </div>

        {isDesktop && faqsToShow[selectedFaqIndex]?.fields?.answer && (
          <div className={classNames('answer')} style={answerStyles}>
            <div className={classNames('answer__question')}>
              {faqsToShow[selectedFaqIndex].fields.question}
            </div>
            <div
              className={classNames('answer__text')}
              style={answerTextStyles}>
              <ContentfulRichText
                field={{ 'en-US': faqsToShow[selectedFaqIndex].fields.answer }}
              />
            </div>
            {faqsToShow[selectedFaqIndex].fields.cta && (
              <Link
                style={linkStylesDesktop}
                component={faqsToShow[selectedFaqIndex].fields.cta}
              />
            )}
          </div>
        )}
      </div>
      {component.fields.cta && (
        <div className={classNames('cta')}>
          <Link
            component={component.fields.cta}
            style={getStylesByPrefix('--cta-', style)}
          />
        </div>
      )}
    </div>
  );
}
